package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.background
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddMenu
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.projects.ProjectsConfirmDelete
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer
import androidx.compose.runtime.LaunchedEffect
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddCheckbox
import org.teddapp.project.ui.TeddScreenHeader
import org.teddapp.project.ui.TeddEmptyState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.foundation.layout.PaddingValues
import com.tedd.db.Portfolio_piece
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddSpace

/**
 * Portfolio & recueil de compétences. Deux onglets : le recueil (Famille → Compétence →
 * Critères, auto-évalués sur quatre niveaux) et le portfolio (pièces racontées à la
 * main, reliées aux compétences qu'elles démontrent). Une action Exporter sort le tout
 * en Markdown, et en PDF quand la plateforme le permet.
 *
 * Un seul écran pour toutes les tailles : colonnes côte à côte sur grand écran,
 * liste puis détail sur téléphone.
 */
@Composable
fun PortfolioScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
    initialTab: PortfolioTab = PortfolioTab.Recueil,
) {
    val model = rememberPortfolioModel(repos)
    remember(model) { model.tab = initialTab }
    model.session = org.teddapp.project.server.LocalServerSession.current
    rememberPortfolioImageLoader()
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        CompositionLocalProvider(LocalUseFullScreenForm provides (wc == WindowClass.Compact)) {
            Box(modifier = Modifier.fillMaxSize()) {
                when (wc) {
                    WindowClass.Compact -> PortfolioCompact(model, onNavigate)
                    else -> PortfolioExpanded(model, onNavigate)
                }
                TeddSnackbarHost(model.snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = if (wc == WindowClass.Compact) 96.dp else 24.dp))
            }
            PortfolioDialogs(model)
        }
    }
}

// ── Dialogues ──────────────────────────────────────────────────────────────

/**
 * Publier une pièce sur le site : sa langue et son adresse, puis le serveur en fige une
 * copie. Sans le compte qui tient le site, on ne peut que marquer la pièce ici.
 */
@Composable
private fun PublishPieceDialog(p: Portfolio_piece, model: PortfolioModel) {
    val supra = model.session?.isSupra == true
    var lang by remember(p.id) { mutableStateOf("fr") }
    var slug by remember(p.id) { mutableStateOf(org.teddapp.project.ui.admin.slugify(p.title)) }
    androidx.compose.ui.window.Dialog(onDismissRequest = { if (!model.publishBusy) model.publishing = null }) {
        TeddCard(modifier = Modifier.widthIn(max = 520.dp).fillMaxWidth(), title = "Publier sur le site", icon = "globe", padding = PaddingValues(TeddSpace.S5)) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                Text(p.title, style = TeddType.BodyStrong, color = TeddTokens.Ink)
                if (supra) {
                    Text("Le site reçoit une copie figée de la pièce, images comprises. Modifier la pièce ensuite ne change rien au site tant que tu ne la republies pas.", style = TeddType.Caption, color = TeddTokens.InkMuted)
                    Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        org.teddapp.project.ui.TeddFilterChip(label = "Français", selected = lang == "fr", onClick = { lang = "fr" })
                        org.teddapp.project.ui.TeddFilterChip(label = "English", selected = lang == "en", onClick = { lang = "en" })
                    }
                    TeddField(label = "Adresse sur le site", value = slug, onValueChange = { slug = org.teddapp.project.ui.admin.slugify(it, typing = true) }, placeholder = "mon-projet")
                    if (slug.isNotEmpty()) Text("/$lang/portfolio/${org.teddapp.project.ui.admin.slugify(slug)}", style = TeddType.Caption, color = TeddTokens.InkMuted)
                    model.publishError?.let { Text(it, color = TeddTokens.Danger, style = TeddType.Caption) }
                    Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        TeddButton(
                            text = if (model.publishBusy) "Publication…" else "Publier",
                            onClick = { model.publishOnSite(p, lang, org.teddapp.project.ui.admin.slugify(slug)) },
                            kind = TeddButtonKind.Publish,
                            enabled = !model.publishBusy && slug.isNotEmpty(),
                        )
                        TeddButton(text = "Annuler", onClick = { model.publishing = null }, kind = TeddButtonKind.Ghost, enabled = !model.publishBusy)
                    }
                } else {
                    Text(
                        "Publier sur le site demande d'être connecté au serveur avec le compte qui tient le site (Réglages). Ici, tu peux seulement marquer la pièce comme publiée.",
                        style = TeddType.Caption, color = TeddTokens.InkMuted,
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        TeddButton(text = "Marquer comme publiée", onClick = { model.markPublished(p, true) })
                        TeddButton(text = "Annuler", onClick = { model.publishing = null }, kind = TeddButtonKind.Ghost)
                    }
                }
            }
        }
    }
}

@Composable
private fun PortfolioDialogs(model: PortfolioModel) {
    model.publishing?.let { PublishPieceDialog(it, model) }
    when (val e = model.editor) {
        is PortfolioEditor.Family -> FamilyEditorDialog(e.existing, model)
        is PortfolioEditor.Competence -> CompetenceEditorDialog(e.existing, e.familyId, model)
        is PortfolioEditor.Criterion -> CriterionEditorDialog(e.existing, e.competenceId, model)
        is PortfolioEditor.Piece -> PieceEditorDialog(e.existing, model)
        null -> Unit
    }
    model.confirmDelete?.let { target ->
        val (title, description) = when (target) {
            is DeleteTarget.Family -> "Supprimer la famille ?" to "Ses compétences, leurs critères et les rattachements de pièces partent avec elle."
            is DeleteTarget.Competence -> "Supprimer la compétence ?" to "Ses critères et les rattachements de pièces partent avec elle."
            is DeleteTarget.Criterion -> "Supprimer le critère ?" to "Les pièces qui le visaient restent rattachées à la compétence entière."
            is DeleteTarget.Piece -> "Supprimer la pièce ?" to "Son récit et ses rattachements sont supprimés."
        }
        ProjectsConfirmDelete(
            title = title,
            itemName = target.name,
            description = description,
            onCancel = { model.confirmDelete = null },
            onConfirm = {
                when (target) {
                    is DeleteTarget.Family -> model.deleteFamily(target.row)
                    is DeleteTarget.Competence -> model.deleteCompetence(target.row)
                    is DeleteTarget.Criterion -> model.deleteCriterion(target.row)
                    is DeleteTarget.Piece -> model.deletePiece(target.row)
                }
            },
        )
    }
}

// ── En-tête commun ─────────────────────────────────────────────────────────

/** Onglets Recueil / Portfolio. */
@Composable
private fun PortfolioTabs(tab: PortfolioTab, onChange: (PortfolioTab) -> Unit, modifier: Modifier = Modifier) {
    TeddTabs(
        tabs = PortfolioTab.entries.map { TeddTab(it.name, it.label) },
        selected = tab.name,
        onSelect = { onChange(PortfolioTab.valueOf(it)) },
        modifier = modifier,
    )
}

/** Bouton Exporter et son menu : Markdown partout, PDF quand la plateforme l'offre. */
@Composable
private fun ExportMenu(model: PortfolioModel) {
    val clipboard = LocalClipboardManager.current
    Box {
        TeddIconButton(onClick = { model.exportOpen = true }, size = 32.dp, active = model.exportOpen) {
            TeddIcon(name = "print", size = 15.dp, color = if (model.exporting) TeddTokens.InkSubtle else TeddTokens.InkMuted)
        }
        val entries = buildList {
            ExportKind.entries.forEach { kind ->
                add(TeddMenuEntry("${kind.label} · Markdown" + if (!model.markdownToFile) " (copier)" else "", icon = "copy", keepOpen = true, onClick = { model.export(kind, pdf = false) { clipboard.setText(AnnotatedString(it)) } }))
                if (model.pdfAvailable) add(TeddMenuEntry("${kind.label} · PDF", icon = "print", keepOpen = true, onClick = { model.export(kind, pdf = true) { } }))
            }
            add(TeddMenuEntry(
                "Inclure les brouillons",
                keepOpen = true,
                leading = { TeddCheckbox(checked = model.exportDrafts, onCheckedChange = { model.exportDrafts = it }) },
                onClick = { model.exportDrafts = !model.exportDrafts },
            ))
            if (!model.pdfAvailable) add(TeddMenuEntry("PDF : sur l'ordinateur seulement", enabled = false, onClick = {}))
        }
        TeddMenu(expanded = model.exportOpen, onDismiss = { model.exportOpen = false }, entries = entries, width = 300.dp, alignEnd = true)
    }
}

@Composable
private fun PortfolioHeader(model: PortfolioModel, compact: Boolean) {
    if (compact) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text("Portfolio", style = TeddType.Title1, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
            PortfolioTabs(model.tab, onChange = { model.tab = it })
            ExportMenu(model)
        }
        return
    }
    TeddScreenHeader(
        title = "Portfolio",
        subtitle = when (model.tab) {
            PortfolioTab.Recueil -> "Ce que tu sais faire, par famille, critère par critère, évalué par toi."
            PortfolioTab.Portfolio -> "Ce que tu as réalisé, raconté, et les compétences que ça démontre."
        },
        actions = {
            PortfolioTabs(model.tab, onChange = { model.tab = it })
            ExportMenu(model)
        },
    )
}

// ── Grand écran ────────────────────────────────────────────────────────────

@Composable
private fun PortfolioExpanded(model: PortfolioModel, onNavigate: (Section) -> Unit) {
    val drawer = rememberSettingsDrawer()
    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            TeddNavRail(active = Section.Portfolio, onNavigate = onNavigate, onSettingsClick = drawer::open)
            Column(modifier = Modifier.weight(1f).fillMaxHeight()) {
                Box(modifier = Modifier.fillMaxWidth().padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S5)) {
                    PortfolioHeader(model, compact = false)
                }
                Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
                when (model.tab) {
                    PortfolioTab.Recueil -> RecueilExpanded(model)
                    PortfolioTab.Portfolio -> PiecesExpanded(model)
                }
            }
        }
    }
}

/** Trait vertical entre deux colonnes. */
@Composable
private fun ColumnRule() {
    Box(Modifier.width(1.dp).fillMaxHeight().background(TeddTokens.Line))
}

/** Les familles : 232 dp par défaut, de 180 à 360. */
private val FAMILIES_PANE = org.teddapp.project.ui.PaneSpec("portfolio.families", default = 232.dp, min = 180.dp, max = 360.dp, collapseBelow = 140.dp)

/** La liste des compétences du recueil : 320 dp par défaut, de 240 à 520. */
private val COMPETENCES_PANE = org.teddapp.project.ui.PaneSpec("portfolio.competences", default = 320.dp, min = 240.dp, max = 520.dp)

/** La liste des pièces : 340 dp par défaut, de 260 à 560. */
private val PIECES_PANE = org.teddapp.project.ui.PaneSpec("portfolio.pieces", default = 340.dp, min = 260.dp, max = 560.dp)

@Composable
private fun RecueilExpanded(model: PortfolioModel) {
    // Sur grand écran, la colonne de droite n'a pas à rester vide : la première
    // compétence de la liste s'ouvre d'elle-même.
    val visible = model.competencesOf(model.selectedFamilyId)
    LaunchedEffect(visible.firstOrNull()?.id, model.selectedCompetence == null) {
        if (model.selectedCompetence == null) model.selectedCompetenceId = visible.firstOrNull()?.id
    }
    Row(modifier = Modifier.fillMaxSize()) {
        // Les filets entre les colonnes sont les poignées : on y règle leur largeur.
        org.teddapp.project.ui.TeddSidePane(
            spec = FAMILIES_PANE,
            title = "les familles",
            collapsedContent = { expand ->
                org.teddapp.project.ui.TeddPaneRail(
                    expand = expand,
                    title = "les familles",
                    items = listOf(org.teddapp.project.ui.PaneRailItem("all", "Toutes les compétences", "list", model.selectedFamilyId == null) { model.selectedFamilyId = null }) +
                        model.families.map { f ->
                            org.teddapp.project.ui.PaneRailItem(
                                f.id, f.name, f.icon, model.selectedFamilyId == f.id,
                                color = runCatching { androidx.compose.ui.graphics.Color(("FF" + f.color.trimStart('#')).toLong(16)) }.getOrNull(),
                            ) { model.selectedFamilyId = f.id }
                        },
                )
            },
        ) {
            Column(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                FamiliesColumn(model)
            }
        }
        org.teddapp.project.ui.TeddSidePane(
            spec = COMPETENCES_PANE,
            title = "la liste des compétences",
            collapsedContent = { expand ->
                org.teddapp.project.ui.TeddPaneRail(
                    expand = expand,
                    title = "la liste des compétences",
                    items = visible.map { c ->
                        org.teddapp.project.ui.PaneRailItem(c.id, c.name, "target", model.selectedCompetenceId == c.id) { model.selectedCompetenceId = c.id }
                    },
                )
            },
        ) {
            Column(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S1),
            ) {
                CompetencesHeader(model, compact = false)
                Spacer(modifier = Modifier.height(TeddSpace.S2))
                CompetencesList(model, compact = false) { model.selectedCompetenceId = it.id }
            }
        }
        Box(
            modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState())
                .padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S12),
        ) {
            Box(modifier = Modifier.widthIn(max = 720.dp)) {
                val c = model.selectedCompetence
                if (c == null) {
                    if (model.competences.isNotEmpty()) {
                        TeddEmptyState(
                            title = "Choisis une compétence",
                            message = "Ses critères et les pièces qui la démontrent s'affichent ici.",
                            icon = "portfolio",
                        )
                    }
                } else {
                    CompetenceDetail(c, model, compact = false)
                }
            }
        }
    }
}

@Composable
private fun PiecesExpanded(model: PortfolioModel) {
    val visible = model.visiblePieces
    LaunchedEffect(visible.firstOrNull()?.id, model.selectedPiece == null) {
        if (model.selectedPiece == null) model.selectedPieceId = visible.firstOrNull()?.id
    }
    Row(modifier = Modifier.fillMaxSize()) {
        org.teddapp.project.ui.TeddSidePane(
            spec = PIECES_PANE,
            title = "la liste des pièces",
            collapsedContent = { expand ->
                org.teddapp.project.ui.TeddPaneRail(
                    expand = expand,
                    title = "la liste des pièces",
                    items = visible.map { p ->
                        org.teddapp.project.ui.PaneRailItem(p.id, p.title, "portfolio", model.selectedPieceId == p.id) { model.selectedPieceId = p.id }
                    },
                )
            },
        ) {
            Column(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S1),
            ) {
                PiecesHeader(model, compact = false)
                Spacer(modifier = Modifier.height(TeddSpace.S2))
                PiecesList(model, compact = false) { model.selectedPieceId = it.id }
            }
        }
        Box(
            modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState())
                .padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S12),
        ) {
            Box(modifier = Modifier.widthIn(max = 720.dp)) {
                val p = model.selectedPiece
                if (p == null) {
                    if (model.pieces.isNotEmpty()) {
                        TeddEmptyState(
                            title = "Choisis une pièce",
                            message = "Son récit et les compétences qu'elle démontre s'affichent ici.",
                            icon = "cards",
                        )
                    }
                } else {
                    PieceDetail(p, model, compact = false)
                }
            }
        }
    }
}

// ── Téléphone ──────────────────────────────────────────────────────────────

@Composable
private fun PortfolioCompact(model: PortfolioModel, onNavigate: (Section) -> Unit) {
    val competence = model.selectedCompetence
    val piece = model.selectedPiece
    val showingDetail = (model.tab == PortfolioTab.Recueil && competence != null) || (model.tab == PortfolioTab.Portfolio && piece != null)

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            Column(modifier = Modifier.fillMaxWidth().statusBarsPadding().padding(horizontal = 16.dp, vertical = 10.dp)) {
                PortfolioHeader(model, compact = true)
            }
            Column(
                modifier = Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState())
                    .padding(start = 16.dp, end = 16.dp, top = 4.dp, bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                when (model.tab) {
                    PortfolioTab.Recueil -> {
                        if (competence != null) {
                            CompetenceDetail(competence, model, compact = true, onBack = { model.selectedCompetenceId = null })
                        } else {
                            if (model.families.isNotEmpty()) FamilyChipsRow(model)
                            if (model.families.isNotEmpty()) CompetencesHeader(model, compact = true)
                            CompetencesList(model, compact = true) { model.selectedCompetenceId = it.id }
                        }
                    }
                    PortfolioTab.Portfolio -> {
                        if (piece != null) {
                            PieceDetail(piece, model, compact = true, onBack = { model.selectedPieceId = null })
                        } else {
                            if (model.pieces.isNotEmpty()) PiecesHeader(model, compact = true)
                            PiecesList(model, compact = true) { model.selectedPieceId = it.id }
                        }
                    }
                }
            }
            TeddBottomNav(active = Section.Portfolio, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }
        // Le bouton flottant crée ce qui a du sens là où on est : un critère dans une
        // compétence ouverte, sinon une compétence (ou une famille s'il n'y en a pas), sinon une pièce.
        if (!showingDetail || model.tab == PortfolioTab.Recueil) {
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .navigationBarsPadding()
                    .padding(end = 16.dp, bottom = 88.dp)
                    .size(56.dp)
                    .clip(CircleShape)
                    .background(TeddTokens.Action, CircleShape)
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                        model.editor = when (model.tab) {
                            PortfolioTab.Recueil -> when {
                                competence != null -> PortfolioEditor.Criterion(null, competence.id)
                                model.families.isEmpty() -> PortfolioEditor.Family(null)
                                else -> PortfolioEditor.Competence(null, model.selectedFamilyId ?: model.families.first().id)
                            }
                            PortfolioTab.Portfolio -> PortfolioEditor.Piece(null)
                        }
                    },
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "plus", size = 22.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
            }
        }
        Spacer(modifier = Modifier.height(0.dp))
    }
}
